import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ActionCard } from "@/components/ui/action-card";
import {
  Building2,
  FileText,
  Calculator,
  TrendingUp,
  Upload,
  Settings,
  Download,
  CheckCircle2,
  Tag,
  BarChart3,
  Sparkles,
  CircleDollarSign,
  Wand2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { getLoginUrl } from "@/const";
import { CompanySwitcher } from "@/components/CompanySwitcher";
import { TemplateWidget } from "@/components/TemplateWidget";
import { MetricsCards } from "@/components/Dashboard";
import DashboardLayout from "@/components/DashboardLayout";
import { useIsMobile } from "@/hooks/useMobile";
import { OnboardingWizard } from "@/components/Onboarding/OnboardingWizard";
import { useState, useEffect, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { trackEvent } from "@/lib/monitoring";
import type { LucideIcon } from "lucide-react";

/**
 * Action definition type for quick actions
 */
interface QuickAction {
  icon: LucideIcon;
  title: string;
  description: string;
  href: string;
  gradient: "primary" | "success" | "neutral";
}

/**
 * All Available Quick Actions
 *
 * Semantic Gradient Mapping (3 variants):
 * - primary: Main CTAs, important actions, data operations
 * - success: Positive actions, uploads, completed states
 * - neutral: Secondary actions, settings, informational
 */
const allQuickActions: QuickAction[] = [
  {
    icon: Building2,
    title: "Manage Companies",
    description: "View and manage your companies",
    href: "/companies",
    gradient: "primary",
  },
  {
    icon: Upload,
    title: "Upload Documents",
    description: "Import bank statements and invoices",
    href: "/documents",
    gradient: "success",
  },
  {
    icon: Download,
    title: "Bank Import",
    description: "Import transactions from Swiss banks",
    href: "/bank-import",
    gradient: "primary",
  },
  {
    icon: CheckCircle2,
    title: "Reconciliation",
    description: "Match bank and system transactions",
    href: "/reconciliation",
    gradient: "success",
  },
  {
    icon: Tag,
    title: "Categorization Rules",
    description: "Manage automatic categorization",
    href: "/categorization-rules",
    gradient: "neutral",
  },
  {
    icon: BarChart3,
    title: "Analytics",
    description: "View insights and rule effectiveness",
    href: "/categorization-analytics",
    gradient: "primary",
  },
  {
    icon: Sparkles,
    title: "AI Suggestions",
    description: "Get AI-powered categorization rules",
    href: "/rule-suggestions",
    gradient: "neutral",
  },
  {
    icon: FileText,
    title: "Transactions",
    description: "View and categorize transactions",
    href: "/transactions",
    gradient: "neutral",
  },
  {
    icon: TrendingUp,
    title: "Financial Statements",
    description: "Generate reports and statements",
    href: "/statements",
    gradient: "neutral",
  },
  {
    icon: Calculator,
    title: "Tax Reports",
    description: "Swiss VAT and corporate tax compliance",
    href: "/tax",
    gradient: "primary",
  },
  {
    icon: CircleDollarSign,
    title: "Tax Optimization",
    description: "AI-powered Swiss tax deduction optimization",
    href: "/tax-optimization",
    gradient: "primary",
  },
  {
    icon: Wand2,
    title: "Company Discovery",
    description: "AI-powered company profile extraction",
    href: "/company-discovery",
    gradient: "success",
  },
  {
    icon: Settings,
    title: "Settings",
    description: "Configure your platform settings",
    href: "/user-profile",
    gradient: "neutral",
  },
];

/**
 * Company state types for context-aware quick actions
 */
type CompanyState = "new_user" | "has_company" | "has_transactions";

/**
 * Get contextual quick actions based on company state
 * Returns 6 most relevant actions for the user's current situation
 */
function getContextualActions(
  state: CompanyState,
  allActions: QuickAction[]
): QuickAction[] {
  const actionsByTitle = Object.fromEntries(allActions.map(a => [a.title, a]));

  switch (state) {
    case "new_user":
      // New users: focus on setup and getting started
      return [
        actionsByTitle["Manage Companies"],
        actionsByTitle["Company Discovery"],
        actionsByTitle["Upload Documents"],
        actionsByTitle["Bank Import"],
        actionsByTitle["Transactions"],
        actionsByTitle["Settings"],
      ].filter(Boolean) as QuickAction[];

    case "has_company":
      // Has company but no transactions: focus on importing data
      return [
        actionsByTitle["Bank Import"],
        actionsByTitle["Upload Documents"],
        actionsByTitle["Company Discovery"],
        actionsByTitle["Transactions"],
        actionsByTitle["Categorization Rules"],
        actionsByTitle["Settings"],
      ].filter(Boolean) as QuickAction[];

    case "has_transactions":
      // Mature: focus on analysis, reconciliation, and reporting
      return [
        actionsByTitle["Reconciliation"],
        actionsByTitle["Analytics"],
        actionsByTitle["Financial Statements"],
        actionsByTitle["Tax Reports"],
        actionsByTitle["Tax Optimization"],
        actionsByTitle["Settings"],
      ].filter(Boolean) as QuickAction[];

    default:
      // Fallback to first 6 actions
      return allActions.slice(0, 6);
  }
}

export default function Dashboard() {
  const { user, isAuthenticated, loading } = useAuth();
  const isMobile = useIsMobile();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showAllActions, setShowAllActions] = useState(false);

  // Check onboarding status
  const onboardingStatus = trpc.onboarding.getStatus.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  // Show onboarding wizard if user hasn't completed it
  useEffect(() => {
    if (onboardingStatus.data && !onboardingStatus.data.completed) {
      setShowOnboarding(true);
    }
  }, [onboardingStatus.data]);

  // Determine company state for contextual actions
  const companyState: CompanyState = useMemo(() => {
    if (!onboardingStatus.data) return "new_user";
    if (!onboardingStatus.data.hasCompany) return "new_user";
    if (!onboardingStatus.data.hasTransactions) return "has_company";
    return "has_transactions";
  }, [onboardingStatus.data]);

  // Get contextual primary actions (6 max)
  const primaryActions = useMemo(
    () => getContextualActions(companyState, allQuickActions),
    [companyState]
  );

  // Secondary actions are everything not in primary
  const secondaryActions = useMemo(() => {
    const primaryHrefs = new Set(primaryActions.map(a => a.href));
    return allQuickActions.filter(a => !primaryHrefs.has(a.href));
  }, [primaryActions]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.href = getLoginUrl();
    return null;
  }

  /**
   * Analytics tracking for quick action clicks
   * Tracks user engagement with dashboard actions via Firebase Analytics
   */
  const handleActionClick = (actionName: string) => {
    trackEvent("dashboard_action_clicked", {
      action: actionName,
      user_id: user?.id,
    });
  };

  return (
    <DashboardLayout>
      {/* Onboarding Wizard */}
      <OnboardingWizard
        open={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={() => {
          setShowOnboarding(false);
          onboardingStatus.refetch();
        }}
      />

      <div className="space-y-10">
        {/* Welcome Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Welcome back, {user?.name || "User"}
            </h1>
            <p className="text-muted-foreground mt-2">
              Manage your Swiss corporate tax compliance with ease
            </p>
          </div>
          {!isMobile && <CompanySwitcher />}
        </div>

        {/* Show onboarding CTA if not completed */}
        {onboardingStatus.data && !onboardingStatus.data.completed && (
          <Card className="border-[var(--primary)]/50 bg-[var(--primary)]/5">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Get Started in 3 Steps
              </CardTitle>
              <CardDescription>
                Set up your company and import your first transactions to unlock
                the full power of the platform
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <Button onClick={() => setShowOnboarding(true)}>
                Start Setup Wizard
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Template Widget */}
        <TemplateWidget />

        {/* Dashboard Metrics with Trend Indicators */}
        <MetricsCards />

        {/* Quick Actions - Context-Aware (6 primary + expandable secondary) */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Quick Actions</h2>
            {secondaryActions.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllActions(!showAllActions)}
                className="text-muted-foreground hover:text-foreground"
              >
                {showAllActions ? (
                  <>
                    Show less <ChevronUp className="ml-1 h-4 w-4" />
                  </>
                ) : (
                  <>
                    View all actions <ChevronDown className="ml-1 h-4 w-4" />
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Primary Actions Grid (6 contextual actions) */}
          <div className="action-card-grid grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {primaryActions.map(action => (
              <ActionCard
                key={action.href}
                icon={action.icon}
                title={action.title}
                description={action.description}
                href={action.href}
                gradient={action.gradient}
                onActionClick={handleActionClick}
              />
            ))}
          </div>

          {/* Secondary Actions (expandable) */}
          {showAllActions && secondaryActions.length > 0 && (
            <div className="pt-4 border-t border-border/50">
              <p className="text-sm text-muted-foreground mb-4">More actions</p>
              <div className="action-card-grid grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {secondaryActions.map(action => (
                  <ActionCard
                    key={action.href}
                    icon={action.icon}
                    title={action.title}
                    description={action.description}
                    href={action.href}
                    gradient={action.gradient}
                    onActionClick={handleActionClick}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Platform Features Overview */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle>Platform Features</CardTitle>
            <CardDescription>
              Comprehensive Swiss corporate tax management
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
                <div>
                  <p className="font-medium">Automated Categorization</p>
                  <p className="text-sm text-muted-foreground">
                    AI-powered transaction categorization with Swiss accounting
                    rules
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
                <div>
                  <p className="font-medium">Bank Reconciliation</p>
                  <p className="text-sm text-muted-foreground">
                    Automatic matching of bank statements with transactions
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
                <div>
                  <p className="font-medium">Swiss VAT Compliance</p>
                  <p className="text-sm text-muted-foreground">
                    Accurate VAT calculations with Swiss rates (8.1%, 2.6%,
                    3.8%)
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
                <div>
                  <p className="font-medium">Financial Statements</p>
                  <p className="text-sm text-muted-foreground">
                    Generate balance sheets, P&L, and cash flow statements
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
                <div>
                  <p className="font-medium">Tax Optimization</p>
                  <p className="text-sm text-muted-foreground">
                    AI suggestions for tax deductions and optimization
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
                <div>
                  <p className="font-medium">Multi-Company Support</p>
                  <p className="text-sm text-muted-foreground">
                    Manage multiple companies with separate accounts
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
